<script setup lang="ts">
import { convertProFormaToSalesInvoice } from '@/api/proFormas';
import SayErrorResponse from '@/mixins/sayErrorResponse';
import Say from '@/utils/Say'
import {inject, onMounted, ref} from "vue";
import {useI18n} from "vue-i18n";
import { jobFilesIndex } from '@/api/jobs'
import { Sortable } from "sortablejs-vue3";
import FinalizeSalesInvoice from './FinalizeSalesInvoice.vue';
import { finalizeSalesInvoice } from '@/api/salesInvoices';

const {t} = useI18n()

const emit = defineEmits(['convertedToSalesInvoice', 'cancelForm', 'loading'])

const salesInvoiceToFinalize = ref([] as any[])

const emailsAreSentToLoggedInUser = inject('emailsAreSentToLoggedInUser', false)
const userEmailAddress = inject('userEmailAddress', false)

const props = defineProps({
  invoice: {
    type: Object,
    required: true
  },
  job: {
    type: Object,
    required: true
  }
})
const form = ref(null as HTMLFormElement | null)
const formValid = ref(false)
const jobFiles = ref([])
const clientReference = ref('')
const invoiceHasClientReference = ref(true)
const remarks = ref('')
const invoiceHasRemarks = ref(false)
const formRules = ref({
  clientReference: [
    value => {
      if (invoiceHasClientReference.value && value.length > 0) return true
      if (!invoiceHasClientReference.value && value.length <= 0) return true
      return t('pleaseEnterAClientReference')
    },
  ],
  remarks: [
    value => {
      if (invoiceHasRemarks.value && value.length > 0) return true
      if (!invoiceHasRemarks.value && value.length <= 0) return true
      return t('pleaseEnterRemarks')
    },
  ],
})
const attachments = ref([])
const jobId = inject('jobId', 0)
const shouldSendEmail = ref(true)

const dialogTitle = inject('dialogTitle', null)
onMounted(() => {
  dialogTitle.value = t('convertProFormaToSalesInvoice')

  try {
    jobFilesIndex(jobId.value).then(response => {
      let files = response.data.uploadedFiles;
      files = files.filter(file => file.extension === 'pdf');
      // Loop through files and set a custom ID for the checkbox (if file has a workOrderId, make it wo-<workOrderId>, otherwise make it j-<fileId>)
      files.forEach(file => {
        file.custom_id = ( file.workOrderId ? `wo-` : `j-` ) + file.id;
      });

      jobFiles.value = files;
      formSubmitted.value = false
    })
  } catch (error) {
    SayErrorResponse(error);
  }

})

const formSubmitted = ref(true)
const submit = () => {

  if(!salesInvoiceToFinalize.value.id) {

  //  Update the attachments array to reflect the jobFiles order
  const selectedAttachments = jobFiles.value
    .filter(file => file.custom_id && attachments.value.includes(file.custom_id))
    .map(file => file.custom_id);
  attachments.value = selectedAttachments;

  form.value.validate()

  setTimeout(() => {

    if (form.value.validate() && !formValid.value) {
      return
    }

    if (formSubmitted.value) {
      return
    }
    
    formSubmitted.value = true
    emit('loading', true)

    convertProFormaToSalesInvoice(props.invoice.id, {
      clientReference: clientReference.value,
      invoiceHasClientReference: invoiceHasClientReference.value,
      remarks: remarks.value,
      invoiceHasRemarks: invoiceHasRemarks.value,
      attachments: attachments.value,
      shouldSendEmail: shouldSendEmail.value
    })
      .then(response => {
        if (response.data.error || !response.data.id) {
          SayErrorResponse(response);
          emit('loading', false)
          formSubmitted.value = false
        } else {
          salesInvoiceToFinalize.value = response.data;
          formSubmitted.value = false
          emit('loading', false)
        }
      })
      .catch(error => {
        SayErrorResponse(error);
        formSubmitted.value = false
        emit('loading', false)
      });
  }, 1)

  } else {
    
    formSubmitted.value = true
    emit('loading', true)

    finalizeSalesInvoice(salesInvoiceToFinalize.value.id)
      .then(response => {
        if (response.data.error) {
          SayErrorResponse(response);
          emit('loading', false)
          formSubmitted.value = false
        } else {
          setTimeout(() => {
            Say('success', t('salesInvoiceFinalizedSuccessfully'));
            emit('convertedToSalesInvoice')
            formSubmitted.value = false
          }, 2000)
        }
      })
      .catch(error => {
        SayErrorResponse(error);
        formSubmitted.value = false
        emit('loading', false)
      });
  }
}
const cancel = () => {
  emit('cancelForm')
}
defineExpose({submit, cancel})

const sortableEnd = (event: any) => {
  // Remove the oldIndex from the jobFiles array
  const movedFile = jobFiles.value.splice(event.oldIndex, 1)[0];

  // Insert the moved file at the new index
  jobFiles.value.splice(event.newIndex, 0, movedFile);

  //  Update the attachments array to reflect the jobFiles order
  const selectedAttachments = jobFiles.value
    .filter(file => file.custom_id && attachments.value.includes(file.custom_id))
    .map(file => file.custom_id);
  attachments.value = selectedAttachments;
}
</script>

<template>
  <div>
    <v-form
      v-model="formValid"
      ref="form"
      class="overflow-auto"
    >
      <template v-if="job.client.exact_online_account_id === null">
        <v-alert type="warning" class="mb-5" density="compact">
          {{ t('clientIsNotLinkedToAnExactOnlineAccount') }}
          <router-link
            :to="{ name: 'ClientsEdit', params: { id: job.client.id } }"
            class="text-white"
          >
            {{ t('clickHereToLinkClientToAnExactOnlineAccount') }}
          </router-link>
        </v-alert>
      </template>
      <template v-else-if="job.client.exact_online_account.email == null || job.client.exact_online_account.email == ''">
        <v-alert type="warning" class="mb-5" density="compact">
          {{ t('exactOnlineAccountHasNoEmailAddress', { name: job.client.exact_online_account.code }) }}
          {{ t('pleaseCompleteItInExactOnline') }}
          {{ t('whenCompleted') }},
          <router-link
            :to="{ name: 'ClientsList' }"
            class="text-white"
          >
            {{ t('goToThisPageToSynchronizeChanges') }}
          </router-link>
        </v-alert>
      </template>
      <template v-else>
        <FinalizeSalesInvoice
          v-if="salesInvoiceToFinalize.id"
          :invoice="salesInvoiceToFinalize"
          :email="job.client.exact_online_account.email"
        />
        <v-row v-else class="my-0">
          <v-col cols="6">

            <!-- <p class="mb-5" v-if="invoice.id">
              {{ t('period') }}:<br>
              <b>
                {{ formatDate(invoice.start_date) }}
                {{ t('upToAndIncludingAbbreviation') }}
                {{ formatDate(invoice.end_date) }}
              </b>
            </p> -->
            <p class="mb-5" v-if="invoice.id">
              {{ t('salesInvoiceBasedOnProForma') }}:<br>
              <b>
                {{ invoice.number }}
              </b>
            </p>
            <p class="mb-5" v-if="invoice.id">
              {{ t('exactOnlineAccount') }}:<br>
              <b>
                {{ job.client.exact_online_account.code }} - {{ job.client.exact_online_account.name }}<br/>
                <!-- {{ job.client.exact_online_account.full_address }} -->
              </b>
            </p>
            <!-- <p class="mb-5" v-if="invoice.id">
              {{ t('invoiceWillBeEmailedTo') }}:<br>
              <b>
                <template v-if="job.client.exact_online_account.email">
                  {{ job.client.exact_online_account.email }}
                </template>
                <span v-else class="text-red">
                  {{ t('noEmailAddressAvailable') }}
                </span>
              </b>
            </p> -->

            <p>{{$t('invoiceHasClientReference')}}:</p>
            <v-checkbox
              v-model="invoiceHasClientReference"
              :label="t('invoiceHasClientReference')"
              hide-details
            />

            <p v-if="invoiceHasClientReference" class="mb-5 -mt-2">
              <v-text-field
                v-model="clientReference"
                :label="t('clientReference')"
                :placeholder="t('clientReferencePlaceholder')"
                variant="underlined"
                maxlength="30"
                :rules="formRules.clientReference"
                hide-details
                />
            </p>

            <p>{{$t('invoiceHasRemarks')}}:</p>
            <v-checkbox
              v-model="invoiceHasRemarks"
              :label="t('invoiceHasRemarks')"
              hide-details
            />

            <p v-if="invoiceHasRemarks" class="mb-5 -mt-2">
              <v-text-field
                v-model="remarks"
                :label="t('remarks')"
                :placeholder="t('remarksPlaceholder')"
                variant="underlined"
                maxlength="150"
                :rules="formRules.remarks"
                hide-details
                />
            </p>

            <p>{{ t('sendSalesInvoiceEmail') }}</p>
            <v-checkbox
                v-model="shouldSendEmail"
                :label="t('sendSalesInvoiceEmail')"
                :disabled="formSubmitted"
                hide-details
            />

            <template v-if="shouldSendEmail">
              <p class="mb-2" v-if="invoice.id">
                {{ t('invoiceWillBeEmailedTo') }}:<br>
                <b>
                    <template v-if="job.client.exact_online_account.email">
                      {{ job.client.exact_online_account.email }}
                    </template>
                    <span v-else class="text-red">
                      {{ t('noEmailAddressAvailable') }}
                    </span>
                  </b>
              </p>
            </template>
          </v-col>

          <v-col cols="6">
            <p>
              {{ t('attachPdfFiles') }}:<br>
            </p>
            <div class="attachmentsWrapper">
              <v-checkbox
                :model-value="true"
                hide-details
                class="attach-files-checkbox"
                readonly
              >
              <template v-slot:label>
                {{ invoice.number }}.pdf
              </template>
              </v-checkbox>
              <template v-if="jobFiles.length > 0">
                <Sortable
                  :list="jobFiles"
                  item-key="id"
                  tag="div"
                  @end="(event) => sortableEnd(event)"
                >
                  <template #item="{element, index}">
                    <div class="attachmentDraggableWrapper">
                      <div class="icon-wrapper">
                        <v-icon class="mr-2" size="16">mdi-drag</v-icon>
                      </div>
                      <v-checkbox
                        v-model="attachments"
                        :value="element.custom_id"
                        hide-details
                        class="attach-files-checkbox cursor-pointer"
                        size="small"
                      >
                        <template v-slot:label>
                          <a :href="element.url" target="_blank" class="link z-50">{{ element.name }}.{{ element.extension }}</a>
                          <span v-if="element.workOrderId" class="ml-2 work-order-badge">{{ t('workOrder') }} {{ element.workOrderId }}</span>
                        </template>
                      </v-checkbox>
                    </div>
                  </template>
                </Sortable>
              </template>
              <template v-else>
                <p class="ml-3 my-3"><i>{{ t('noAdditionalUploadedJobFilesAvailable') }}</i></p>
              </template>
            </div>
          </v-col>
          <v-col cols="12" class="py-0">
            <p>
              {{ t('goToPreviewSalesInvoice') }}
            </p>
            <template v-if="shouldSendEmail">
              <v-alert v-if="emailsAreSentToLoggedInUser" type="info" density="compact" class="mt-5">
                  {{ t('emailWillBeSentToLoggedInUser', {'emailAddress':userEmailAddress}) }}
              </v-alert>
            </template>
          </v-col>
        </v-row>
      </template>
    </v-form>
  </div>
</template>

<style scoped lang="scss">
.link {
  color: rgb(30, 136, 229);
  text-decoration: underline;
}
.attachmentsWrapper {
  padding: 2px 0 6px 0;
  margin: 6px 0 12px 0;
  border: 1px solid #e0e0e0;
  max-height: 339px;
  padding-right: 6px;
  overflow-y: auto;
}
.attachmentsWrapper .attach-files-checkbox:first-child {
  padding: 2px 12px 2px 32px;
}
.attach-files-checkbox {
  padding: 2px 12px 2px 0;
}
:deep(.attach-files-checkbox label) {
  opacity: 1;
  margin-left: 12px;
}
:deep(.attach-files-checkbox .v-selection-control--density-default) {
  --v-selection-control-size: 10;
}

.attachmentDraggableWrapper {
  background-color: #fff;
  overflow-y: auto;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}
.attachmentDraggableWrapper .icon-wrapper {
  padding: 5px 0 4px 8px;
  float: left;
}
.attachmentDraggableWrapper .icon-wrapper .v-icon {
  cursor: move;
}
.attachmentDraggableWrapper.sortable-ghost {
  background-color: #ccc;
  opacity: 0.5;
}
.attachmentDraggableWrapper.sortable-drag {
  background-color: #ccc;
}

.work-order-badge {
  background-color: #F57828 !important;
  color: #FFFFFF !important;
  border-radius: 3px;
  padding:0 5px;
  text-transform: uppercase;
  white-space: nowrap;
}
.-mt-2 {
  margin-top: -8px !important;
}
</style>
